import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {ContextualMenu} from '@Components/contextual-menu';
import {SeparatorType} from '@PosterWhiteboard/items/tabs-item/tabs-item.class';
import {useUpdateEffect} from 'ahooks';
import type {ColorFillStorage} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import type {RGBA} from '@Utils/color.util';
import {getColorObjectArrayFromFillColor} from '@Utils/color.util';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getAssetUrl} from '@Utils/s3.util';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './poster-editor-tab-item-separator-panel.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromTabItem} from '@Hooks/poster-editor/useGetKeyFromTabItem';

interface PosterEditorTabItemSeparatorPanelProps {
  panelId: string;
}

enum PanelToShow {
  COLOR_PICKER = 'COLOR_PICKER',
  DEFAULT = 'DEFAULT',
}

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/separator/';

export default function PosterEditorTabItemSeparatorPanel({...props}: PosterEditorTabItemSeparatorPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const [panelToShow, setPanelToShow] = useState(PanelToShow.DEFAULT);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const itemSeparatorType = useGetKeyFromTabItem('separatorType') as SeparatorType;
  const itemSeparatorColor = useGetKeyFromTabItem('separatorColor');

  useUpdateEffect((): void => {
    onClose();
  }, [activeItem]);

  useEffect((): void => {
    if (panelToShow === PanelToShow.DEFAULT) {
      updateHeight(activeItem?.isTab() ? itemSeparatorType : SeparatorType.NONE);
    }
  }, [panelToShow, activeItemId]);

  const updateHeight = (type: SeparatorType): void => {
    if (type === SeparatorType.NONE) {
      updateOptionsPanelHeight(props.panelId, '158px');
    } else {
      updateOptionsPanelHeight(props.panelId, '206px');
    }
  };

  if (!activeItem?.isTab() || itemSeparatorType === undefined || !itemSeparatorColor) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_separator')} />;
  };

  const onClickHandler = (id: SeparatorType): void => {
    if (itemSeparatorType === id) {
      return;
    }

    updateHeight(id);
    void activeItem.updateFromObject({
      separatorType: id,
    });
  };

  const getData = (): ColorFillStorage[] => {
    return [
      {
        id: 'seperator-color-object',
        color: getColorObjectArrayFromFillColor([itemSeparatorColor]),
      },
    ];
  };

  const onClickColorFill = (): void => {
    setPanelToShow(PanelToShow.COLOR_PICKER);
  };

  const getSubOptions = (): ReactElement => {
    return (
      <ContextualMenuColorFillOption
        isDisabled={itemSeparatorType === SeparatorType.NONE}
        id="separator-type-color-option"
        label={window.i18next.t('pmwjs_color')}
        data={getData()}
        onClick={onClickColorFill}
        className="spacing-m-b-4"
      />
    );
  };

  const getThumbnails = (): ReactElement => {
    return (
      <ul className="flex-h-row spacing-m-0">
        <ContextualMenuThumbnailItem
          key="none"
          thumbnail={<Icon icon="icon-ban" size={IconSize.SIZE_ICON_32} />}
          selectionState={ThumbnailItemSelectionState.BORDER}
          isSelected={itemSeparatorType === SeparatorType.NONE}
          text={window.i18next.t('pmwjs_none')}
          onClick={(): void => {
            onClickHandler(SeparatorType.NONE);
          }}
        />
        <ContextualMenuThumbnailItem
          key="solid"
          thumbnail={
            <Thumbnail
              className={`${styles.thumb} spacing-p-2`}
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}solid.png`),
              }}
            />
          }
          isSelected={itemSeparatorType === SeparatorType.SOLID}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_solid_line')}
          onClick={(): void => {
            onClickHandler(SeparatorType.SOLID);
          }}
        />
        <ContextualMenuThumbnailItem
          key="dashed"
          thumbnail={
            <Thumbnail
              className={`${styles.thumb} spacing-p-2`}
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}dashed.png`),
              }}
            />
          }
          isSelected={itemSeparatorType === SeparatorType.DASHED}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_dashed')}
          onClick={(): void => {
            onClickHandler(SeparatorType.DASHED);
          }}
        />
      </ul>
    );
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="poster-editor-tab-separator-options" hasMobileModalPadding hasMobileMorphingProperties>
        {itemSeparatorType === SeparatorType.NONE ? null : getSubOptions()}
        {getThumbnails()}
      </ContextualMenu>
    );
  };

  const updateFillColor = (fillColor: RGBA[], undoable = true): void => {
    void activeItem.updateFromObject(
      {
        separatorColor: fillColor[0],
      },
      {undoable}
    );
  };

  const getColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        panelId={props.panelId}
        fillObject={{
          fillType: FillTypes.SOLID,
          fillColor: [itemSeparatorColor],
        }}
        onBackButton={(): void => {
          setPanelToShow(PanelToShow.DEFAULT);
        }}
        onChangeColor={updateFillColor}
        onClose={onClose}
      />
    );
  };

  const getDefaultPanel = (): ReactElement => {
    switch (panelToShow) {
      case PanelToShow.COLOR_PICKER:
        return getColorPanel();
      case PanelToShow.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
    }
  };

  return getDefaultPanel();
}
